import { useCallback, useEffect, useLayoutEffect, useState } from 'preact/hooks';
import { RefObject } from 'preact';
import { useUiContext } from '../contexts/UiContext';

interface VirtualListItemSize {
  itemSize: number[];
  setItemSize: (index: number, size: number) => void;
  getItemSize: (index: number) => number;
}

// used by the VirtualList component
// cache the list items size and provide a method to add sizes
export const useVirtualList = (itemCount: number, estimatedItemSize: number): VirtualListItemSize => {
  const [itemSize, setItemsSize] = useState<number[]>(new Array(itemCount).fill(estimatedItemSize));

  const setItemSize = useCallback(
    (index: number, size: number): void => {
      if (itemSize[index] !== size) {
        setItemsSize((items) => {
          const arr = items.slice();
          arr[index] = size;

          return arr;
        });
      }
    },
    [itemSize],
  );

  const getItemSize = useCallback(
    (index: number): number => {
      return itemSize[index];
    },
    [itemSize],
  );

  return { getItemSize, itemSize, setItemSize };
};

interface VirtualListItemProps {
  index: number;
  itemRef: RefObject<HTMLDivElement>;
  getItemSize: (index: number) => number;
  setItemSize: (index: number, height: number) => void;
}

interface VirtualListItemReturn {
  updateComponentSize: () => void;
}

// used by the VirtualList items to control the item size
export const useVirtualListItem = ({
  itemRef,
  index,
  getItemSize,
  setItemSize,
}: VirtualListItemProps): VirtualListItemReturn => {
  const [itemHeight, setItemHeight] = useState(() => 0);
  const { previousLanguage, currentLanguage } = useUiContext();

  // Set initial item height
  useLayoutEffect(() => {
    let height;

    if (itemHeight === 0 || (currentLanguage !== '' && currentLanguage !== previousLanguage)) {
      height = itemRef.current?.offsetHeight;

      if (height) {
        setItemHeight(height);
        setItemSize(index, height);
      }
    } else {
      height = getItemSize(index);
    }
  }, [index, itemHeight, itemRef, setItemSize, getItemSize, previousLanguage, currentLanguage]);

  // update the item size when show or hide extra content
  const updateComponentSize = useCallback((): void => {
    const height = itemRef?.current?.offsetHeight;

    if (height) {
      setItemSize(index, height);
    }
  }, [index, itemRef, setItemSize]);

  useEffect(() => {
    const handleResize = (): void => {
      updateComponentSize();
    };

    window.addEventListener('resize', handleResize, true);

    return (): void => {
      window.removeEventListener('resize', handleResize, true);
    };
  }, [updateComponentSize]);

  return { updateComponentSize };
};
